import { useState, useEffect, useRef } from 'react'
import { Label } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Upload, FileText, X } from 'lucide-react'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useItemStore } from '../../../../stores/itemStore'
import { useEnemyStore } from '../../../../stores/enemyStore'
import { useRelationshipStore } from '../../../../stores/relationshipStore'
import { usePlotStore } from '../../../../stores/plotStore'
import { useNameStore } from '../../../../stores/nameStore'
import { useTitleStore } from '../../../../stores/titleStore'
import { useWikipediaStore } from '../../../../stores/wikipediaStore'
import { useImagePromptStore } from '../../../../stores/imagePromptStore'
import { useSkillStore } from '../../../../stores/skillStore'
import { useDungeonStore } from '../../../../stores/dungeonStore'
import { useCommunityStore } from '../../../../stores/communityStore'
import { useSocietyStore } from '../../../../stores/societyStore'
import { useBuildingStore } from '../../../../stores/buildingStore'
import { useLocationStore } from '../../../../stores/locationStore'
import { useDocumentStore } from '../../../../stores/documentStore'
import { useSummaryStore } from '../../../../stores/summaryStore'
import { readMultipleFiles, FILE_ACCEPT, formatFileSize } from '../../utils/fileReader'
import type { DialogueContext } from '@creanizer/types'

interface GeneratorContextProps {
  onContextChange: (context: DialogueContext) => void
  customContext: string
  onCustomContextChange: (value: string) => void
  // stateとsetterをpropsとして受け取る
  uploadedFiles: { id: string; name: string; content: string; size: number }[]
  setUploadedFiles: React.Dispatch<React.SetStateAction<{ id: string; name: string; content: string; size: number }[]>>
  primaryWorldId: string
  setPrimaryWorldId: (id: string) => void
  secondaryWorldId: string
  setSecondaryWorldId: (id: string) => void
  dataMode: 'selected' | 'none'
  setDataMode: (mode: 'selected' | 'none') => void
  selectedCharacterIds: Set<string>
  setSelectedCharacterIds: (ids: Set<string>) => void
  selectedItemIds: Set<string>
  setSelectedItemIds: (ids: Set<string>) => void
  selectedEnemyIds: Set<string>
  setSelectedEnemyIds: (ids: Set<string>) => void
  selectedRelationshipIds: Set<string>
  setSelectedRelationshipIds: (ids: Set<string>) => void
  selectedPlotIds: Set<string>
  setSelectedPlotIds: (ids: Set<string>) => void
  selectedTitleIds: Set<string>
  setSelectedTitleIds: (ids: Set<string>) => void
  selectedNameIds: Set<string>
  setSelectedNameIds: (ids: Set<string>) => void
  selectedWikipediaIds: Set<string>
  setSelectedWikipediaIds: (ids: Set<string>) => void
  selectedImagePromptIds: Set<string>
  setSelectedImagePromptIds: (ids: Set<string>) => void
  selectedSkillIds: Set<string>
  setSelectedSkillIds: (ids: Set<string>) => void
  selectedDungeonIds: Set<string>
  setSelectedDungeonIds: (ids: Set<string>) => void
  selectedCommunityIds: Set<string>
  setSelectedCommunityIds: (ids: Set<string>) => void
  selectedSocietyIds: Set<string>
  setSelectedSocietyIds: (ids: Set<string>) => void
  selectedBuildingIds: Set<string>
  setSelectedBuildingIds: (ids: Set<string>) => void
  selectedLocationIds: Set<string>
  setSelectedLocationIds: (ids: Set<string>) => void
  selectedDocumentIds: Set<string>
  setSelectedDocumentIds: (ids: Set<string>) => void
  selectedSummaryIds: Set<string>
  setSelectedSummaryIds: (ids: Set<string>) => void
}

export function GeneratorContext({
  onContextChange,
  customContext,
  onCustomContextChange,
  // propsからstateとsetterを受け取る
  uploadedFiles,
  setUploadedFiles,
  primaryWorldId,
  setPrimaryWorldId,
  secondaryWorldId,
  setSecondaryWorldId,
  dataMode,
  setDataMode,
  selectedCharacterIds,
  setSelectedCharacterIds,
  selectedItemIds,
  setSelectedItemIds,
  selectedEnemyIds,
  setSelectedEnemyIds,
  selectedRelationshipIds,
  setSelectedRelationshipIds,
  selectedPlotIds,
  setSelectedPlotIds,
  selectedTitleIds,
  setSelectedTitleIds,
  selectedNameIds,
  setSelectedNameIds,
  selectedWikipediaIds,
  setSelectedWikipediaIds,
  selectedImagePromptIds,
  setSelectedImagePromptIds,
  selectedSkillIds,
  setSelectedSkillIds,
  selectedDungeonIds,
  setSelectedDungeonIds,
  selectedCommunityIds,
  setSelectedCommunityIds,
  selectedSocietyIds,
  setSelectedSocietyIds,
  selectedBuildingIds,
  setSelectedBuildingIds,
  selectedLocationIds,
  setSelectedLocationIds,
  selectedDocumentIds,
  setSelectedDocumentIds,
  selectedSummaryIds,
  setSelectedSummaryIds
}: GeneratorContextProps) {
  const worldStore = useWorldBuilderStore()
  const characterStore = useCharacterStore()
  const itemStore = useItemStore()
  const enemyStore = useEnemyStore()
  const relationshipStore = useRelationshipStore()
  const plotStore = usePlotStore()
  const nameStore = useNameStore()
  const titleStore = useTitleStore()
  const wikipediaStore = useWikipediaStore()
  const imagePromptStore = useImagePromptStore()
  const skillStore = useSkillStore()
  const dungeonStore = useDungeonStore()
  const communityStore = useCommunityStore()
  const societyStore = useSocietyStore()
  const buildingStore = useBuildingStore()
  const locationStore = useLocationStore()
  const documentStore = useDocumentStore()
  const summaryStore = useSummaryStore()

  const fileInputRef = useRef<HTMLInputElement>(null)
  // stateは親コンポーネントからprops経由で受け取る

  useEffect(() => {
    // 構造化されたコンテキストを構築
    console.log('[GeneratorContext] Building context with:', {
      dataMode,
      primaryWorldId,
      secondaryWorldId,
      selectedCharacterIds: selectedCharacterIds.size,
      selectedItemIds: selectedItemIds.size,
      uploadedFiles: uploadedFiles.length,
      customContext: customContext?.substring(0, 100)
    })

    const context: DialogueContext = {}

    // 世界観コンテキスト
    if (primaryWorldId !== 'none') {
      const primaryWorld = worldStore.savedWorlds?.find(w => w.id === primaryWorldId)
      if (primaryWorld) {
        context.worldContext = JSON.stringify(primaryWorld.result)
      }
    }

    if (secondaryWorldId !== 'none') {
      const secondaryWorld = worldStore.savedWorlds?.find(w => w.id === secondaryWorldId)
      if (secondaryWorld) {
        context.worldContextSecondary = JSON.stringify(secondaryWorld.result)
      }
    }

    // データモードに応じてデータを追加
    if (dataMode === 'selected') {
      // 選択されたデータのみ使用
      const selectedCharacters = characterStore.characters.filter(c => selectedCharacterIds.has(c.id))
      if (selectedCharacters.length > 0) {
        context.characters = selectedCharacters
      }

      const selectedItems = itemStore.items.filter(i => selectedItemIds.has(i.id))
      if (selectedItems.length > 0) {
        context.items = selectedItems
      }

      const selectedEnemies = enemyStore.enemies.filter(e => selectedEnemyIds.has(e.id))
      if (selectedEnemies.length > 0) {
        context.enemies = selectedEnemies
      }

      const selectedRelationships = relationshipStore.savedRelationships?.filter(r => selectedRelationshipIds.has(r.id))
      if (selectedRelationships?.length > 0) {
        context.relationships = selectedRelationships
      }

      const selectedPlots = plotStore.savedPlots?.filter(p => selectedPlotIds.has(p.id))
      if (selectedPlots?.length > 0) {
        context.plots = selectedPlots
      }

      const selectedTitles = titleStore.savedTitles?.filter(t => selectedTitleIds.has(t.id))
      if (selectedTitles?.length > 0) {
        context.titles = selectedTitles
      }

      const selectedNames = nameStore.savedNames?.filter(n => selectedNameIds.has(n.id))
      if (selectedNames?.length > 0) {
        context.names = selectedNames
      }

      const selectedWikipedia = wikipediaStore.savedWikipedias?.filter(w => selectedWikipediaIds.has(w.id))
      if (selectedWikipedia?.length > 0) {
        context.articles = selectedWikipedia
      }

      const selectedImagePrompts = imagePromptStore.savedPrompts?.filter(i => selectedImagePromptIds.has(i.id))
      if (selectedImagePrompts?.length > 0) {
        context.images = selectedImagePrompts
      }

      const selectedSkills = skillStore.skills?.filter(s => selectedSkillIds.has(s.id))
      if (selectedSkills?.length > 0) {
        context.skills = selectedSkills
      }

      const selectedDungeons = dungeonStore.dungeons?.filter(d => selectedDungeonIds.has(d.id))
      if (selectedDungeons?.length > 0) {
        context.dungeons = selectedDungeons
      }

      const selectedCommunities = communityStore.savedCommunities?.filter(c => selectedCommunityIds.has(c.id))
      if (selectedCommunities?.length > 0) {
        context.communities = selectedCommunities
      }

      const selectedSocieties = societyStore.savedSocieties?.filter(s => selectedSocietyIds.has(s.id))
      if (selectedSocieties?.length > 0) {
        context.societies = selectedSocieties
      }

      const selectedBuildings = buildingStore.buildings?.filter(b => selectedBuildingIds.has(b.id))
      if (selectedBuildings?.length > 0) {
        context.buildings = selectedBuildings
      }

      const selectedLocations = locationStore.locations?.filter(l => selectedLocationIds.has(l.id))
      if (selectedLocations?.length > 0) {
        context.locations = selectedLocations
      }

      const selectedDocuments = documentStore.documents?.filter(d => selectedDocumentIds.has(d.id))
      if (selectedDocuments?.length > 0) {
        context.documents = selectedDocuments
      }

      const selectedSummaries = summaryStore.summaries?.filter(s => selectedSummaryIds.has(s.id))
      if (selectedSummaries?.length > 0) {
        context.summaries = selectedSummaries
      }
    }

    // アップロードされたファイルのコンテンツを追加
    if (uploadedFiles.length > 0) {
      const filesContent = uploadedFiles
        .map(file => `【ファイル: ${file.name}】\n${file.content}`)
        .join('\n\n')
      // ファイルコンテンツをカスタムコンテキストに含める
      const existingCustom = context.customContext || ''
      context.customContext = existingCustom ? `${existingCustom}\n\n${filesContent}` : filesContent
    }

    // カスタムコンテキストを追加
    if (customContext && customContext.trim()) {
      const existingCustom = context.customContext || ''
      context.customContext = existingCustom ? `${existingCustom}\n\n${customContext}` : customContext
    }

    console.log('[GeneratorContext] Context built:', {
      hasWorldContext: !!context.worldContext,
      hasWorldContextSecondary: !!context.worldContextSecondary,
      charactersCount: context.characters?.length || 0,
      itemsCount: context.items?.length || 0,
      hasCustomContext: !!context.customContext
    })

    onContextChange(context)
  }, [
    primaryWorldId,
    secondaryWorldId,
    dataMode,
    selectedCharacterIds,
    selectedItemIds,
    selectedEnemyIds,
    selectedRelationshipIds,
    selectedPlotIds,
    selectedTitleIds,
    selectedNameIds,
    selectedWikipediaIds,
    selectedImagePromptIds,
    selectedSkillIds,
    selectedDungeonIds,
    selectedCommunityIds,
    selectedSocietyIds,
    selectedBuildingIds,
    selectedLocationIds,
    selectedDocumentIds,
    selectedSummaryIds,
    uploadedFiles,
    customContext,
    worldStore.savedWorlds,
    characterStore.characters,
    itemStore.items,
    enemyStore.enemies,
    relationshipStore.savedRelationships,
    plotStore.savedPlots,
    titleStore.savedTitles,
    nameStore.savedNames,
    wikipediaStore.savedWikipedias,
    imagePromptStore.savedPrompts,
    skillStore.skills,
    dungeonStore.dungeons,
    communityStore.savedCommunities,
    societyStore.savedSocieties,
    buildingStore.buildings,
    locationStore.locations,
    documentStore.documents,
    summaryStore.summaries,
    onContextChange
  ])

  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    try {
      const { combinedText, fileInfos, errors } = await readMultipleFiles(files)

      // エラーがある場合は表示
      if (errors.length > 0) {
        alert(`以下のファイルの読み込みに失敗しました:\n${errors.join('\n')}`)
      }

      // 成功したファイルを追加
      const successfulFiles = fileInfos
        .filter(info => !info.error && info.text)
        .map(info => ({
          id: `file_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          name: info.fileName,
          content: info.text,
          size: 0 // サイズは後で計算可能
        }))

      setUploadedFiles(prev => [...prev, ...successfulFiles])
    } catch (error) {
      console.error('ファイル読み込みエラー:', error)
      alert('ファイルの読み込みに失敗しました')
    }

    // ファイル選択をリセット
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleFileRemove = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId))
  }

  const toggleSelection = <T extends string>(
    selectedSet: Set<T>,
    setSelected: (set: Set<T>) => void,
    id: T
  ) => {
    const newSet = new Set(selectedSet)
    if (newSet.has(id)) {
      newSet.delete(id)
    } else {
      newSet.add(id)
    }
    setSelected(newSet)
  }

  return (
    <div className="space-y-4">
      {/* ファイルアップロード */}
      <div className="space-y-2">
        <Label className="text-xs">テキストファイル</Label>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          className="w-full"
        >
          <Upload className="h-4 w-4 mr-2" />
          ファイルをインポート
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept={FILE_ACCEPT}
          multiple
          onChange={handleFileImport}
          className="hidden"
        />

        {/* アップロード済みファイルのリスト */}
        {uploadedFiles.length > 0 && (
          <div className="mt-2 p-2 bg-gray-50 rounded-lg border">
            <div className="text-xs font-medium text-gray-700 mb-1">読み込み済み：</div>
            <div className="space-y-1">
              {uploadedFiles.map((file) => (
                <div key={file.id} className="flex items-center justify-between bg-white p-1 rounded border">
                  <div className="flex items-center gap-1">
                    <FileText className="h-3 w-3 text-gray-500" />
                    <span className="text-xs">{file.name}</span>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => handleFileRemove(file.id)}
                    className="p-0.5 h-5 w-5"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              合計 {uploadedFiles.length} ファイル
            </div>
          </div>
        )}
      </div>

      {/* 世界観選択（最大2つ） */}
      <div className="space-y-2">
        <Label className="text-xs">世界観設定</Label>
        {!worldStore.savedWorlds || worldStore.savedWorlds.length === 0 ? (
          <div className="text-xs text-muted-foreground p-2 border rounded">
            保存済み世界観がありません。世界観ビルダーで世界観を作成してください。
          </div>
        ) : (
          <div className="space-y-2">
            <div>
              <Label className="text-xs text-muted-foreground">メイン世界観</Label>
              <Select
                value={primaryWorldId}
                onValueChange={setPrimaryWorldId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="選択..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">なし</SelectItem>
                  {worldStore.savedWorlds?.map((world) => (
                    <SelectItem key={world.id} value={world.id}>
                      {world.result.name || 'Unknown'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">比較世界観（任意）</Label>
              <Select
                value={secondaryWorldId}
                onValueChange={setSecondaryWorldId}
                disabled={primaryWorldId === 'none'}
              >
                <SelectTrigger>
                  <SelectValue placeholder="比較用..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">なし</SelectItem>
                  {worldStore.savedWorlds
                    ?.filter(w => w.id !== primaryWorldId)
                    .map((world) => (
                      <SelectItem key={world.id} value={world.id}>
                        {world.result.name || 'Unknown'}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        {secondaryWorldId !== 'none' && (
          <p className="text-xs text-blue-500">
            比較検討モード: 2つの世界観を比較分析します
          </p>
        )}
      </div>

      {/* データ選択 */}
      <div className="space-y-2">
        <Label className="text-xs">参照データ選択</Label>
        <Tabs value={dataMode} onValueChange={(v) => setDataMode(v as any)}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="none">使用しない</TabsTrigger>
            <TabsTrigger value="selected">選択</TabsTrigger>
          </TabsList>

          <TabsContent value="selected" className="space-y-2 mt-2">
            {/* キャラクター選択 */}
            <div>
              <Label className="text-xs">キャラクター</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!characterStore.characters || characterStore.characters.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済みキャラクターがありません</div>
                ) : (
                  characterStore.characters.map(character => (
                    <div key={character.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`char-${character.id}`}
                        checked={selectedCharacterIds.has(character.id)}
                        onCheckedChange={() => toggleSelection(selectedCharacterIds, setSelectedCharacterIds, character.id)}
                      />
                      <Label
                        htmlFor={`char-${character.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {character.result.name || 'Unknown'}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* アイテム選択 */}
            <div>
              <Label className="text-xs">アイテム</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!itemStore.items || itemStore.items.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済みアイテムがありません</div>
                ) : (
                  itemStore.items.map(item => (
                    <div key={item.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`item-${item.id}`}
                        checked={selectedItemIds.has(item.id)}
                        onCheckedChange={() => toggleSelection(selectedItemIds, setSelectedItemIds, item.id)}
                      />
                      <Label
                        htmlFor={`item-${item.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {item.result.basicInfo.name || 'Unknown'}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* エネミー選択 */}
            <div>
              <Label className="text-xs">エネミー</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!enemyStore.enemies || enemyStore.enemies.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済みエネミーがありません</div>
                ) : (
                  enemyStore.enemies.map(enemy => (
                    <div key={enemy.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`enemy-${enemy.id}`}
                        checked={selectedEnemyIds.has(enemy.id)}
                        onCheckedChange={() => toggleSelection(selectedEnemyIds, setSelectedEnemyIds, enemy.id)}
                      />
                      <Label
                        htmlFor={`enemy-${enemy.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {enemy.result.basicInfo?.name || 'Unknown'}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* 関係性選択 */}
            <div>
              <Label className="text-xs">関係性</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!relationshipStore.savedRelationships || relationshipStore.savedRelationships.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済み関係性がありません</div>
                ) : (
                  relationshipStore.savedRelationships.map(relationship => (
                    <div key={relationship.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`rel-${relationship.id}`}
                        checked={selectedRelationshipIds.has(relationship.id)}
                        onCheckedChange={() => toggleSelection(selectedRelationshipIds, setSelectedRelationshipIds, relationship.id)}
                      />
                      <Label
                        htmlFor={`rel-${relationship.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {relationship.result?.characters?.[0]?.name || 'Unknown'} ⇔ {relationship.result?.characters?.[1]?.name || 'Unknown'}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* プロット選択 */}
            <div>
              <Label className="text-xs">プロット</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!plotStore.savedPlots || plotStore.savedPlots.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済みプロットがありません</div>
                ) : (
                  plotStore.savedPlots.map(plot => (
                    <div key={plot.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`plot-${plot.id}`}
                        checked={selectedPlotIds.has(plot.id)}
                        onCheckedChange={() => toggleSelection(selectedPlotIds, setSelectedPlotIds, plot.id)}
                      />
                      <Label
                        htmlFor={`plot-${plot.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {plot.result?.title || 'Unknown'}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* タイトル選択 */}
            <div>
              <Label className="text-xs">タイトル</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!titleStore.savedTitles || titleStore.savedTitles.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済みタイトルがありません</div>
                ) : (
                  titleStore.savedTitles.map(title => (
                    <div key={title.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`title-${title.id}`}
                        checked={selectedTitleIds.has(title.id)}
                        onCheckedChange={() => toggleSelection(selectedTitleIds, setSelectedTitleIds, title.id)}
                      />
                      <Label
                        htmlFor={`title-${title.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {title.result?.candidates?.[0]?.title || 'Unknown'}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* 名前選択 */}
            <div>
              <Label className="text-xs">名前</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!nameStore.savedNames || nameStore.savedNames.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済み名前がありません</div>
                ) : (
                  nameStore.savedNames.map(name => (
                    <div key={name.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`name-${name.id}`}
                        checked={selectedNameIds.has(name.id)}
                        onCheckedChange={() => toggleSelection(selectedNameIds, setSelectedNameIds, name.id)}
                      />
                      <Label
                        htmlFor={`name-${name.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {name.name || 'Unknown'}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Wikipedia選択 */}
            <div>
              <Label className="text-xs">Wikipedia</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!wikipediaStore.savedWikipedias || wikipediaStore.savedWikipedias.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済み記事がありません</div>
                ) : (
                  wikipediaStore.savedWikipedias.map(article => (
                    <div key={article.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`wiki-${article.id}`}
                        checked={selectedWikipediaIds.has(article.id)}
                        onCheckedChange={() => toggleSelection(selectedWikipediaIds, setSelectedWikipediaIds, article.id)}
                      />
                      <Label
                        htmlFor={`wiki-${article.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {article.result?.title || 'Unknown'}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* 画像プロンプト選択 */}
            <div>
              <Label className="text-xs">画像プロンプト</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!imagePromptStore.savedPrompts || imagePromptStore.savedPrompts.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済み画像プロンプトがありません</div>
                ) : (
                  imagePromptStore.savedPrompts.map(prompt => (
                    <div key={prompt.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`img-${prompt.id}`}
                        checked={selectedImagePromptIds.has(prompt.id)}
                        onCheckedChange={() => toggleSelection(selectedImagePromptIds, setSelectedImagePromptIds, prompt.id)}
                      />
                      <Label
                        htmlFor={`img-${prompt.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {prompt.title || 'Unknown'}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* スキル選択 */}
            <div>
              <Label className="text-xs">スキル</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!skillStore.skills || skillStore.skills.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済みスキルがありません</div>
                ) : (
                  skillStore.skills.map(skill => (
                    <div key={skill.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`skill-${skill.id}`}
                        checked={selectedSkillIds.has(skill.id)}
                        onCheckedChange={() => toggleSelection(selectedSkillIds, setSelectedSkillIds, skill.id)}
                      />
                      <Label
                        htmlFor={`skill-${skill.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {skill.result?.basicInfo?.name || 'Unknown'}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* ダンジョン選択 */}
            <div>
              <Label className="text-xs">ダンジョン</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!dungeonStore.dungeons || dungeonStore.dungeons.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済みダンジョンがありません</div>
                ) : (
                  dungeonStore.dungeons.map(dungeon => (
                    <div key={dungeon.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`dung-${dungeon.id}`}
                        checked={selectedDungeonIds.has(dungeon.id)}
                        onCheckedChange={() => toggleSelection(selectedDungeonIds, setSelectedDungeonIds, dungeon.id)}
                      />
                      <Label
                        htmlFor={`dung-${dungeon.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {dungeon.result?.basicInfo?.name || 'Unknown'}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* コミュニティ選択 */}
            <div>
              <Label className="text-xs">コミュニティ</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!communityStore.savedCommunities || communityStore.savedCommunities.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済みコミュニティがありません</div>
                ) : (
                  communityStore.savedCommunities.map(community => (
                    <div key={community.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`comm-${community.id}`}
                        checked={selectedCommunityIds.has(community.id)}
                        onCheckedChange={() => toggleSelection(selectedCommunityIds, setSelectedCommunityIds, community.id)}
                      />
                      <Label
                        htmlFor={`comm-${community.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {community.result?.basicInfo?.name || 'Unknown'}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* 社会選択 */}
            <div>
              <Label className="text-xs">社会</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!societyStore.savedSocieties || societyStore.savedSocieties.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済み社会がありません</div>
                ) : (
                  societyStore.savedSocieties.map(society => (
                    <div key={society.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`soc-${society.id}`}
                        checked={selectedSocietyIds.has(society.id)}
                        onCheckedChange={() => toggleSelection(selectedSocietyIds, setSelectedSocietyIds, society.id)}
                      />
                      <Label
                        htmlFor={`soc-${society.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {society.result?.basicInfo?.name || 'Unknown'}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* 建物選択 */}
            <div>
              <Label className="text-xs">建物</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!buildingStore.buildings || buildingStore.buildings.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済み建物がありません</div>
                ) : (
                  buildingStore.buildings.map(building => (
                    <div key={building.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`build-${building.id}`}
                        checked={selectedBuildingIds.has(building.id)}
                        onCheckedChange={() => toggleSelection(selectedBuildingIds, setSelectedBuildingIds, building.id)}
                      />
                      <Label
                        htmlFor={`build-${building.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {building.result?.basicInfo?.name || 'Unknown'}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* 場所選択 */}
            <div>
              <Label className="text-xs">場所</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!locationStore.locations || locationStore.locations.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済み場所がありません</div>
                ) : (
                  locationStore.locations.map(location => (
                    <div key={location.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`loc-${location.id}`}
                        checked={selectedLocationIds.has(location.id)}
                        onCheckedChange={() => toggleSelection(selectedLocationIds, setSelectedLocationIds, location.id)}
                      />
                      <Label
                        htmlFor={`loc-${location.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {location.result?.basicInfo?.name || 'Unknown'}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* ドキュメント選択 */}
            <div>
              <Label className="text-xs">ドキュメント</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!documentStore.documents || documentStore.documents.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済みドキュメントがありません</div>
                ) : (
                  documentStore.documents.map(document => (
                    <div key={document.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`doc-${document.id}`}
                        checked={selectedDocumentIds.has(document.id)}
                        onCheckedChange={() => toggleSelection(selectedDocumentIds, setSelectedDocumentIds, document.id)}
                      />
                      <Label
                        htmlFor={`doc-${document.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {(document.result as any)?.title || (document.result as any)?.basicInfo?.title || 'Unknown'}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* サマリー選択 */}
            <div>
              <Label className="text-xs">サマリー</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!summaryStore.summaries || summaryStore.summaries.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済みサマリーがありません</div>
                ) : (
                  summaryStore.summaries.map(summary => (
                    <div key={summary.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`sum-${summary.id}`}
                        checked={selectedSummaryIds.has(summary.id)}
                        onCheckedChange={() => toggleSelection(selectedSummaryIds, setSelectedSummaryIds, summary.id)}
                      />
                      <Label
                        htmlFor={`sum-${summary.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {summary.title || 'Unknown'}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="none" className="mt-2">
            <div className="text-xs text-muted-foreground">
              保存済みデータを参照せずに対話シミュレーションを行います
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* カスタム設定 */}
      <div className="space-y-2">
        <Label className="text-xs">カスタム設定（任意）</Label>
        <Textarea
          value={customContext}
          onChange={(e) => onCustomContextChange(e.target.value)}
          placeholder="追加の設定や文脈情報を入力してください..."
          className="h-24 text-xs"
        />
      </div>
    </div>
  )
}